/**
 * Device Modal Template Registry — Monolithic modal templates.
 *
 * Each entry maps a template ID to a lazy-loaded React component.
 * Templates declare which device types they support.
 * Legacy trait-composed modals are represented as a fallback entry.
 */
import { lazy, type ComponentType } from 'react'
import type { DeviceModalTemplateEntry, DeviceModalTemplateProps } from '../types/deviceModalTemplate'
import { AC_CLASSIC_SECTIONS } from '../modals/ACModalClassic'
import { AC_FROSTED_SECTIONS } from '../modals/FrostedACModal'
import { AIR_PURIFIER_FROSTED_SECTIONS } from '../modals/FrostedAirPurifierModal'
import { DEHUMIDIFIER_FROSTED_SECTIONS } from '../modals/FrostedDehumidifierModal'
import { ERV_FROSTED_SECTIONS } from '../modals/FrostedERVModal'
import { WATER_PURIFIER_FROSTED_SECTIONS } from '../modals/FrostedWaterPurifierModal'
import { WATER_DISPENSER_FROSTED_SECTIONS } from '../modals/FrostedWaterDispenserModal'
import { WATER_DISPENSER_V2_FROSTED_SECTIONS } from '../modals/FrostedWaterDispenserV2Modal'
import { WATER_DISPENSER_V3_SECTIONS } from '../modals/StandaloneWaterDispenserV3'
import { DEHUMIDIFIER_STANDALONE_V1_SECTIONS } from '../modals/StandaloneDehumidifierV1'
import { FAN_FROSTED_SECTIONS } from '../modals/FrostedFanModal'
import { POWER_METER_FROSTED_SECTIONS } from '../modals/FrostedPowerMeterModal'
import { BLINDS_FROSTED_SECTIONS, CURTAIN_FROSTED_SECTIONS } from '../modals/FrostedCoverModal'
import { LIGHT_FROSTED_SECTIONS } from '../modals/FrostedLightModal'
import { SWITCH_FROSTED_SECTIONS } from '../modals/FrostedSwitchModal'
import { LIGHT_CLASSIC_SECTIONS } from '../modals/LightModalClassic'
import { SENSOR_CLASSIC_SECTIONS } from '../modals/SensorModalClassic'
import { FAN_CLASSIC_SECTIONS } from '../modals/FanModalClassic'
import { SWITCH_CLASSIC_SECTIONS } from '../modals/SwitchModalClassic'
import { GENERIC_SWITCH_CLASSIC_SECTIONS } from '../modals/GenericSwitchModalClassic'
import { BLINDS_CLASSIC_SECTIONS } from '../modals/BlindsModalClassic'
import { CURTAIN_CLASSIC_SECTIONS } from '../modals/CurtainModalClassic'
import { ELECTRICAL_SENSOR_CLASSIC_SECTIONS } from '../modals/ElectricalSensorModalClassic'
import { LIGHT_SENSOR_CLASSIC_SECTIONS } from '../modals/LightSensorModalClassic'
import { OCCUPANCY_SENSOR_CLASSIC_SECTIONS } from '../modals/OccupancySensorModalClassic'
import { CONTACT_SENSOR_CLASSIC_SECTIONS } from '../modals/ContactSensorModalClassic'
import { TEMPERATURE_SENSOR_CLASSIC_SECTIONS } from '../modals/TemperatureSensorModalClassic'
import { HUMIDITY_SENSOR_CLASSIC_SECTIONS } from '../modals/HumiditySensorModalClassic'

// ---------------------------------------------------------------------------
// Built-in templates (lazy loaded)
// ---------------------------------------------------------------------------

const ACModalClassicComponent = lazy(() =>
  import('../modals/ACModalClassic').then((m) => ({ default: m.ACModalClassic }))
)
const FrostedACModalComponent = lazy(() =>
  import('../modals/FrostedACModal').then((m) => ({ default: m.FrostedACModal }))
)
const FrostedAirPurifierModalComponent = lazy(() =>
  import('../modals/FrostedAirPurifierModal').then((m) => ({ default: m.FrostedAirPurifierModal }))
)
const FrostedDehumidifierModalComponent = lazy(() =>
  import('../modals/FrostedDehumidifierModal').then((m) => ({ default: m.FrostedDehumidifierModal }))
)
const FrostedERVModalComponent = lazy(() =>
  import('../modals/FrostedERVModal').then((m) => ({ default: m.FrostedERVModal }))
)
const FrostedWaterPurifierModalComponent = lazy(() =>
  import('../modals/FrostedWaterPurifierModal').then((m) => ({ default: m.FrostedWaterPurifierModal }))
)
const FrostedWaterDispenserModalComponent = lazy(() =>
  import('../modals/FrostedWaterDispenserModal').then((m) => ({ default: m.FrostedWaterDispenserModal }))
)
const FrostedWaterDispenserV2ModalComponent = lazy(() =>
  import('../modals/FrostedWaterDispenserV2Modal').then((m) => ({ default: m.FrostedWaterDispenserV2Modal }))
)
const StandaloneWaterDispenserV3Component = lazy(() =>
  import('../modals/StandaloneWaterDispenserV3').then((m) => ({ default: m.StandaloneWaterDispenserV3 }))
)
const StandaloneDehumidifierV1Component = lazy(() =>
  import('../modals/StandaloneDehumidifierV1').then((m) => ({ default: m.StandaloneDehumidifierV1 }))
)
const FrostedFanModalComponent = lazy(() =>
  import('../modals/FrostedFanModal').then((m) => ({ default: m.FrostedFanModal }))
)
const FrostedPowerMeterModalComponent = lazy(() =>
  import('../modals/FrostedPowerMeterModal').then((m) => ({ default: m.FrostedPowerMeterModal }))
)
const FrostedLightModalComponent = lazy(() =>
  import('../modals/FrostedLightModal').then((m) => ({ default: m.FrostedLightModal }))
)
const FrostedSwitchModalComponent = lazy(() =>
  import('../modals/FrostedSwitchModal').then((m) => ({ default: m.FrostedSwitchModal }))
)
const FrostedBlindsModalComponent = lazy(() =>
  import('../modals/FrostedCoverModal').then((m) => ({ default: m.FrostedBlindsModal }))
)
const FrostedCurtainModalComponent = lazy(() =>
  import('../modals/FrostedCoverModal').then((m) => ({ default: m.FrostedCurtainModal }))
)
const LightModalClassicComponent = lazy(() =>
  import('../modals/LightModalClassic').then((m) => ({ default: m.LightModalClassic }))
)
const SensorModalClassicComponent = lazy(() =>
  import('../modals/SensorModalClassic').then((m) => ({ default: m.SensorModalClassic }))
)
const FanModalClassicComponent = lazy(() =>
  import('../modals/FanModalClassic').then((m) => ({ default: m.FanModalClassic }))
)
const SwitchModalClassicComponent = lazy(() =>
  import('../modals/SwitchModalClassic').then((m) => ({ default: m.SwitchModalClassic }))
)
const GenericSwitchModalClassicComponent = lazy(() =>
  import('../modals/GenericSwitchModalClassic').then((m) => ({ default: m.GenericSwitchModalClassic }))
)
const BlindsModalClassicComponent = lazy(() =>
  import('../modals/BlindsModalClassic').then((m) => ({ default: m.BlindsModalClassic }))
)
const CurtainModalClassicComponent = lazy(() =>
  import('../modals/CurtainModalClassic').then((m) => ({ default: m.CurtainModalClassic }))
)
const ElectricalSensorModalClassicComponent = lazy(() =>
  import('../modals/ElectricalSensorModalClassic').then((m) => ({ default: m.ElectricalSensorModalClassic }))
)
const LightSensorModalClassicComponent = lazy(() =>
  import('../modals/LightSensorModalClassic').then((m) => ({ default: m.LightSensorModalClassic }))
)
const OccupancySensorModalClassicComponent = lazy(() =>
  import('../modals/OccupancySensorModalClassic').then((m) => ({ default: m.OccupancySensorModalClassic }))
)
const ContactSensorModalClassicComponent = lazy(() =>
  import('../modals/ContactSensorModalClassic').then((m) => ({ default: m.ContactSensorModalClassic }))
)
const TemperatureSensorModalClassicComponent = lazy(() =>
  import('../modals/TemperatureSensorModalClassic').then((m) => ({ default: m.TemperatureSensorModalClassic }))
)
const HumiditySensorModalClassicComponent = lazy(() =>
  import('../modals/HumiditySensorModalClassic').then((m) => ({ default: m.HumiditySensorModalClassic }))
)

// ---------------------------------------------------------------------------
// Registry
// ---------------------------------------------------------------------------

const DEVICE_MODAL_TEMPLATES: DeviceModalTemplateEntry[] = [
  {
    id: 'ac-classic',
    name: 'AC Classic',
    deviceTypes: ['AC_UNIT'],
    component: ACModalClassicComponent as ComponentType<DeviceModalTemplateProps>,
    sections: AC_CLASSIC_SECTIONS,
    description: 'Classic AC control with thermostat dial, mode selector, and fan speed',
    author: 'Ultron',
    category: 'standard',
  },
  {
    id: 'ac-frosted',
    name: 'AC Frosted Glass',
    deviceTypes: ['AC_UNIT'],
    component: FrostedACModalComponent as ComponentType<DeviceModalTemplateProps>,
    sections: AC_FROSTED_SECTIONS,
    description: 'Apple Home 風格毛玻璃 AC 控制面板 — 半圓弧溫度、綠色 tint、無 card 邊框',
    author: 'Ultron',
    category: 'standard',
  },
  {
    id: 'air-purifier-frosted',
    name: 'Air Purifier Frosted Glass',
    deviceTypes: ['AIR_PURIFIER', 'AIRPURIFIER'],
    component: FrostedAirPurifierModalComponent as ComponentType<DeviceModalTemplateProps>,
    sections: AIR_PURIFIER_FROSTED_SECTIONS,
    description: 'Apple Home 風格毛玻璃空氣清淨機 — AQI ring、藍色 tint、HEPA filter 狀態卡',
    author: 'Ultron',
    category: 'standard',
  },
  {
    id: 'dehumidifier-frosted',
    name: 'Dehumidifier Frosted Glass',
    deviceTypes: ['DEHUMIDIFIER'],
    component: FrostedDehumidifierModalComponent as ComponentType<DeviceModalTemplateProps>,
    sections: DEHUMIDIFIER_FROSTED_SECTIONS,
    description: 'Apple Home 風格毛玻璃除濕機 — 濕度 ring、紫色 tint、水箱狀態卡',
    author: 'Ultron',
    category: 'standard',
  },
  {
    id: 'erv-frosted',
    name: 'ERV Frosted Glass',
    deviceTypes: ['ULTRON_ERV', 'ERV'],
    component: FrostedERVModalComponent as ComponentType<DeviceModalTemplateProps>,
    sections: ERV_FROSTED_SECTIONS,
    description: 'Apple Home 風格毛玻璃全熱交換機 — CO2 ring、teal tint、Airflow 狀態卡',
    author: 'Ultron',
    category: 'standard',
  },
  {
    id: 'water-purifier-frosted',
    name: 'Water Purifier Frosted Glass',
    deviceTypes: ['WATERPURIFIER', 'WATER_PURIFIER'],
    component: FrostedWaterPurifierModalComponent as ComponentType<DeviceModalTemplateProps>,
    sections: WATER_PURIFIER_FROSTED_SECTIONS,
    description: 'Apple Home 風格毛玻璃淨水器 — TDS ring、indigo tint、三階段濾芯狀態卡',
    author: 'Ultron',
    category: 'standard',
  },
  {
    id: 'water-dispenser-frosted',
    name: 'Water Dispenser Frosted Glass',
    deviceTypes: ['ULTRON_WATER_DISPENSER', 'WATER_DISPENSER'],
    component: FrostedWaterDispenserModalComponent as ComponentType<DeviceModalTemplateProps>,
    sections: WATER_DISPENSER_FROSTED_SECTIONS,
    description: 'Apple Home 風格毛玻璃飲水機 — 1~3 出水口溫度、水位、濾芯購買、兒童鎖',
    author: 'Ultron',
    category: 'standard',
  },
  {
    id: 'water-dispenser-frosted-v2',
    name: 'Water Dispenser Dark Glass V2',
    deviceTypes: ['ULTRON_WATER_DISPENSER', 'WATER_DISPENSER'],
    component: FrostedWaterDispenserV2ModalComponent as ComponentType<DeviceModalTemplateProps>,
    sections: WATER_DISPENSER_V2_FROSTED_SECTIONS,
    description: 'Stitch v2 深色 Glass 飲水機 — 動態水光環、bento grid 資訊卡、溫度選擇器',
    author: 'Ultron',
    category: 'standard',
  },
  {
    id: 'water-dispenser-standalone-v3',
    name: 'Water Dispenser Standalone V3 (Oasis)',
    deviceTypes: ['ULTRON_WATER_DISPENSER', 'WATER_DISPENSER'],
    component: StandaloneWaterDispenserV3Component as ComponentType<DeviceModalTemplateProps>,
    sections: WATER_DISPENSER_V3_SECTIONS,
    description: 'Earthy Oasis fullscreen standalone water dispenser — for Acuo App inline rendering',
    author: 'Ultron',
    category: 'standalone',
  },
  {
    id: 'dehumidifier-standalone-v1',
    name: 'Dehumidifier Standalone V1 (Oasis)',
    deviceTypes: ['DEHUMIDIFIER'],
    component: StandaloneDehumidifierV1Component as ComponentType<DeviceModalTemplateProps>,
    sections: DEHUMIDIFIER_STANDALONE_V1_SECTIONS,
    description: 'Earthy Oasis fullscreen standalone dehumidifier — for Acuo App inline rendering',
    author: 'Ultron',
    category: 'standalone',
  },
  {
    id: 'fan-frosted',
    name: 'Fan Frosted Glass',
    deviceTypes: ['FAN'],
    component: FrostedFanModalComponent as ComponentType<DeviceModalTemplateProps>,
    sections: FAN_FROSTED_SECTIONS,
    description: 'Apple Home 風格毛玻璃風扇 — Speed ring、slate tint、擺頭開關、定時器卡片',
    author: 'Ultron',
    category: 'standard',
  },
  {
    id: 'power-meter-frosted',
    name: 'Power Meter Frosted Glass',
    deviceTypes: ['ULTRON_POWER_METER', 'POWER_METER', 'ENERGY_METER', 'METER'],
    component: FrostedPowerMeterModalComponent as ComponentType<DeviceModalTemplateProps>,
    sections: POWER_METER_FROSTED_SECTIONS,
    description: '毛玻璃電錶 — 自動偵測單相/三相、SVG gauge、R/S/T tab 切換、Voltage/Current/PF',
    author: 'Ultron',
    category: 'standard',
  },
  {
    id: 'blinds-frosted',
    name: 'Blinds Frosted Glass',
    deviceTypes: ['BLINDS'],
    component: FrostedBlindsModalComponent as ComponentType<DeviceModalTemplateProps>,
    sections: BLINDS_FROSTED_SECTIONS,
    description: 'Apple Home 風格毛玻璃百葉窗 — Vertical slider、amber tint、Open/Stop/Close',
    author: 'Ultron',
    category: 'standard',
  },
  {
    id: 'curtain-frosted',
    name: 'Curtain Frosted Glass',
    deviceTypes: ['CURTAIN'],
    component: FrostedCurtainModalComponent as ComponentType<DeviceModalTemplateProps>,
    sections: CURTAIN_FROSTED_SECTIONS,
    description: 'Apple Home 風格毛玻璃窗簾 — Vertical slider、amber tint、Open/Stop/Close',
    author: 'Ultron',
    category: 'standard',
  },
  {
    id: 'light-frosted',
    name: 'Light Frosted Glass',
    deviceTypes: ['LIGHT'],
    component: FrostedLightModalComponent as ComponentType<DeviceModalTemplateProps>,
    sections: LIGHT_FROSTED_SECTIONS,
    description: 'Apple Home 風格毛玻璃燈光 — Brightness slider、warm yellow tint、色溫預設',
    author: 'Ultron',
    category: 'standard',
  },
  {
    id: 'switch-frosted',
    name: 'Switch Frosted Glass',
    deviceTypes: ['SWITCH', 'OUTLET', 'GENERIC_SWITCH'],
    component: FrostedSwitchModalComponent as ComponentType<DeviceModalTemplateProps>,
    sections: SWITCH_FROSTED_SECTIONS,
    description: 'Apple Home 風格毛玻璃開關 — Tap toggle、green tint、可選電力監控卡、icon 可自訂',
    author: 'Ultron',
    category: 'standard',
  },
  {
    id: 'light-classic',
    name: 'Light Classic',
    deviceTypes: ['LIGHT'],
    component: LightModalClassicComponent as ComponentType<DeviceModalTemplateProps>,
    sections: LIGHT_CLASSIC_SECTIONS,
    description: 'Light control with brightness slider, color temperature, and presets',
    author: 'Ultron',
    category: 'standard',
  },
  {
    id: 'sensor-classic',
    name: 'Sensor Classic',
    deviceTypes: ['SENSOR'],
    component: SensorModalClassicComponent as ComponentType<DeviceModalTemplateProps>,
    sections: SENSOR_CLASSIC_SECTIONS,
    description: 'Read-only sensor display with temperature, humidity, air quality cards',
    author: 'Ultron',
    category: 'standard',
  },
  {
    id: 'fan-classic',
    name: 'Fan Classic',
    deviceTypes: ['FAN'],
    component: FanModalClassicComponent as ComponentType<DeviceModalTemplateProps>,
    sections: FAN_CLASSIC_SECTIONS,
    description: 'Fan control with power toggle, speed selector, and oscillation',
    author: 'Ultron',
    category: 'standard',
  },
  {
    id: 'switch-classic',
    name: 'Switch Classic',
    deviceTypes: ['SWITCH', 'OUTLET'],
    component: SwitchModalClassicComponent as ComponentType<DeviceModalTemplateProps>,
    sections: SWITCH_CLASSIC_SECTIONS,
    description: 'Simple on/off switch with large power button',
    author: 'Ultron',
    category: 'standard',
  },
  {
    id: 'generic-switch-classic',
    name: 'Generic Switch Classic',
    deviceTypes: ['GENERIC_SWITCH'],
    component: GenericSwitchModalClassicComponent as ComponentType<DeviceModalTemplateProps>,
    sections: GENERIC_SWITCH_CLASSIC_SECTIONS,
    description: 'Generic smart switch with on/off toggle',
    author: 'Ultron',
    category: 'standard',
  },
  {
    id: 'blinds-classic',
    name: 'Blinds Classic',
    deviceTypes: ['BLINDS'],
    component: BlindsModalClassicComponent as ComponentType<DeviceModalTemplateProps>,
    sections: BLINDS_CLASSIC_SECTIONS,
    description: 'Blinds control with position slider and quick presets',
    author: 'Ultron',
    category: 'standard',
  },
  {
    id: 'curtain-classic',
    name: 'Curtain Classic',
    deviceTypes: ['CURTAIN'],
    component: CurtainModalClassicComponent as ComponentType<DeviceModalTemplateProps>,
    sections: CURTAIN_CLASSIC_SECTIONS,
    description: 'Curtain control with position slider and open/stop/close buttons',
    author: 'Ultron',
    category: 'standard',
  },
  {
    id: 'electrical-sensor-classic',
    name: 'Electrical Sensor',
    deviceTypes: ['ELECTRICAL_SENSOR', 'ELECTRICAL_SENSOR_1P3W', 'ELECTRICAL_SENSOR_3P4W'],
    component: ElectricalSensorModalClassicComponent as ComponentType<DeviceModalTemplateProps>,
    sections: ELECTRICAL_SENSOR_CLASSIC_SECTIONS,
    description: 'Power meter with voltage, current, wattage, and energy consumption',
    author: 'Ultron',
    category: 'standard',
  },
  {
    id: 'light-sensor-classic',
    name: 'Light Sensor',
    deviceTypes: ['LIGHT_SENSOR'],
    component: LightSensorModalClassicComponent as ComponentType<DeviceModalTemplateProps>,
    sections: LIGHT_SENSOR_CLASSIC_SECTIONS,
    description: 'Illuminance display with lux value and brightness level',
    author: 'Ultron',
    category: 'standard',
  },
  {
    id: 'occupancy-sensor-classic',
    name: 'Occupancy Sensor',
    deviceTypes: ['OCCUPANCY_SENSOR'],
    component: OccupancySensorModalClassicComponent as ComponentType<DeviceModalTemplateProps>,
    sections: OCCUPANCY_SENSOR_CLASSIC_SECTIONS,
    description: 'Presence detection with occupied/vacant status',
    author: 'Ultron',
    category: 'standard',
  },
  {
    id: 'contact-sensor-classic',
    name: 'Contact Sensor',
    deviceTypes: ['CONTACT_SENSOR'],
    component: ContactSensorModalClassicComponent as ComponentType<DeviceModalTemplateProps>,
    sections: CONTACT_SENSOR_CLASSIC_SECTIONS,
    description: 'Door/window contact with open/closed status',
    author: 'Ultron',
    category: 'standard',
  },
  {
    id: 'temperature-sensor-classic',
    name: 'Temperature Sensor',
    deviceTypes: ['TEMPERATURE_SENSOR'],
    component: TemperatureSensorModalClassicComponent as ComponentType<DeviceModalTemplateProps>,
    sections: TEMPERATURE_SENSOR_CLASSIC_SECTIONS,
    description: 'Temperature display with comfort level indicator',
    author: 'Ultron',
    category: 'standard',
  },
  {
    id: 'humidity-sensor-classic',
    name: 'Humidity Sensor',
    deviceTypes: ['HUMIDITY_SENSOR'],
    component: HumiditySensorModalClassicComponent as ComponentType<DeviceModalTemplateProps>,
    sections: HUMIDITY_SENSOR_CLASSIC_SECTIONS,
    description: 'Humidity display with comfort level indicator',
    author: 'Ultron',
    category: 'standard',
  },
]

// ---------------------------------------------------------------------------
// Lookup functions
// ---------------------------------------------------------------------------

/**
 * Get all modal templates that support a given device type.
 * @param category - optional: 'standard' | 'standalone'. If provided, filters by category.
 */
export function getModalTemplatesForDeviceType(deviceType: string, category?: 'standard' | 'standalone'): DeviceModalTemplateEntry[] {
  const normalized = String(deviceType || '').trim().toUpperCase()
  return DEVICE_MODAL_TEMPLATES.filter((t) => {
    if (!t.deviceTypes.some((dt) => dt.toUpperCase() === normalized)) return false
    if (category) return (t.category || 'standard') === category
    return true
  })
}

/**
 * Get a specific modal template by ID.
 */
export function getModalTemplateById(id: string): DeviceModalTemplateEntry | null {
  return DEVICE_MODAL_TEMPLATES.find((t) => t.id === id) ?? null
}

/**
 * List all registered modal templates.
 */
export function listModalTemplates(): DeviceModalTemplateEntry[] {
  return [...DEVICE_MODAL_TEMPLATES]
}

/**
 * Check if a device type has any monolithic modal template.
 * If false, fall back to the legacy trait-composed DeviceTypeModalContent.
 */
export function hasMonolithicTemplate(deviceType: string): boolean {
  return getModalTemplatesForDeviceType(deviceType).length > 0
}
